import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.#checkFormValidity()) return;

    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');
    if (!form) return true;

    const allInputs = Array.from(form.querySelectorAll('input, select, textarea')).filter((input) =>
      input.id.includes('Recipient')
    );
    let allInputsValid = true;
    for (const input of allInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      // Skip disabled inputs
      if (input.disabled) continue;

      // Check validity on all input elements
      if (!input.checkValidity()) {
        allInputsValid = false;
        break;
      }
    }
    return allInputsValid;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Prevent overlapping add-to-cart submissions */
  #inFlight = false;

  /** Track whether the button was already disabled before submission */
  #buttonWasDisabled = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);
    // Early exit if a submission is already in flight
    if (this.#inFlight) return;

    // Proper disabled guard (attribute value may be empty string), prevent duplicate rapid clicks
    const atcBtn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (atcBtn && atcBtn.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

  const formData = new FormData(form);

  // Fresh upsell integration: read selection from <variant-picker-product-upsell> (if present)
  // and submit a multi-item payload (or alternative swap) directly.
  try {
    const productId = this.dataset.productId;
    if (productId) {
      const debug = Boolean(window.__upsellPickerDebug);

      // Prefer the nearest upsell picker in the same product details area as the form.
      const productDetailsRoot = this.closest('.product-details');
      const gridRoot = this.closest('[data-product-grid-content]') || this.closest('product-information');

      /** @type {HTMLElement[]} */
      let candidates = [];

      if (productDetailsRoot) {
        candidates = Array.from(productDetailsRoot.querySelectorAll('variant-picker-product-upsell')).filter(
          (n) => n instanceof HTMLElement
        );
      }

      if (!candidates.length && gridRoot) {
        candidates = Array.from(gridRoot.querySelectorAll('variant-picker-product-upsell')).filter((n) => n instanceof HTMLElement);
      }

      if (!candidates.length) {
        candidates = Array.from(document.querySelectorAll('variant-picker-product-upsell')).filter((n) => n instanceof HTMLElement);
      }

      // Filter out quick-add contexts (we want the one relevant to the current form).
      candidates = candidates.filter((el) => !el.closest('quick-add-dialog') && !el.closest('quick-add-component'));

      // If multiple exist, prefer matching product id.
      /** @type {HTMLElement | null} */
      const upsellPicker =
        candidates.find((el) => el.getAttribute('data-product-id') === String(productId)) ||
        candidates[0] ||
        null;

      if (debug) {
        const payload = {
          productId,
          productDetailsRootFound: Boolean(productDetailsRoot),
          gridRootFound: Boolean(gridRoot),
          candidates: candidates.length,
          upsellPickerFound: Boolean(upsellPicker),
          upsellPickerProductId: upsellPicker?.getAttribute('data-product-id') || null,
        };
        window.__lastUpsellPickerDebug = payload;
        console.warn('[UpsellPicker]', payload);
      }

      if (upsellPicker) {
        const selected = /** @type {HTMLInputElement | null} */ (upsellPicker.querySelector('input[type="radio"]:checked'));
        const optionType = selected?.dataset?.optionType || null;

        const mainIdEntry = formData.get('id');
        const mainId = typeof mainIdEntry === 'string' ? mainIdEntry : mainIdEntry ? String(mainIdEntry) : '';
        const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

        const upsell1Id = /** @type {any} */ (upsellPicker).dataset?.upsell1Id;
        const upsell2Id = /** @type {any} */ (upsellPicker).dataset?.upsell2Id;
        const upsell3aId = /** @type {any} */ (upsellPicker).dataset?.upsell3aId;
        const upsell3bId = /** @type {any} */ (upsellPicker).dataset?.upsell3bId;
        const alternativeId = /** @type {any} */ (upsellPicker).dataset?.alternativeId;

        /** @type {{id: string|number, quantity: number}[]} */
        const items = [];

        if (optionType === 'alternative' && alternativeId) {
          items.push({ id: String(alternativeId), quantity });
        } else if (mainId) {
          items.push({ id: mainId, quantity });
        }

        if (debug) {
          const payload = {
            optionType,
            mainId,
            quantity,
            upsell1Id,
            upsell2Id,
            upsell3aId,
            upsell3bId,
            alternativeId,
          };
          window.__lastUpsellPickerSelection = payload;
          console.warn('[UpsellPicker] selection', payload);
        }

        if (items.length) {
          if (optionType === 'option2' && upsell1Id) {
            items.push({ id: String(upsell1Id), quantity: 1 });
          }
          if (optionType === 'option3') {
            if (upsell1Id) items.push({ id: String(upsell1Id), quantity: 1 });
            if (upsell2Id) items.push({ id: String(upsell2Id), quantity: 1 });
          }
          if (optionType === 'option4') {
            if (upsell3aId) items.push({ id: String(upsell3aId), quantity: 1 });
            if (upsell3bId) items.push({ id: String(upsell3bId), quantity: 1 });
          }

          // Only switch to multi-item submit when we actually need it.
          const needsMulti = optionType === 'option2' || optionType === 'option3' || optionType === 'option4';
          const needsSwap = optionType === 'alternative';
          if (needsMulti || needsSwap) {
            // Ensure state matches the normal submit path.
            this.#buttonWasDisabled = !!atcBtn?.disabled;
            this.#inFlight = true;
            this.refs.addToCartButtonContainer?.disable();

            this.#submitMultiple(items, formData);
            return;
          }
        }
      }
    }
  } catch (e) {
    console.warn('Upsell picker integration failed', e);
  }

  // Mark in-flight & disable button (remember prior state so we don't incorrectly enable later)
  this.#buttonWasDisabled = !!atcBtn?.disabled;
  this.#inFlight = true;
  this.refs.addToCartButtonContainer?.disable();

    // Collect upsell checkboxes within the same section and append their IDs
    try {
      const sectionId = this.dataset.sectionId;
      const sectionRoot = sectionId
        ? document.getElementById(`shopify-section-${sectionId}`) || this.closest('.shopify-section')
        : this.closest('.shopify-section');

      // In some contexts (e.g. quick-add modal), product-form-component may not be inside a
      // shopify section element. Fall back to searching within the component itself.
      const upsellRoot = sectionRoot || this;
      const upsellCheckboxes = upsellRoot.querySelectorAll('.upsell-checkbox:checked');
      if (upsellCheckboxes && upsellCheckboxes.length > 0) {
        // Shopify cart/add supports an items[] array; we can submit via JSON body below if needed
        // For multipart/form-data, we will switch to JSON body for multiple items
        const mainIdEntry = formData.get('id');
        const mainId = typeof mainIdEntry === 'string' ? mainIdEntry : mainIdEntry ? String(mainIdEntry) : '';
        if (mainId) {
          const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
          /** @type {{id: string|number, quantity: number}[]} */
          const items = [{ id: mainId, quantity }];
          upsellCheckboxes.forEach((cb) => {
            const input = /** @type {HTMLInputElement} */ (cb);
            items.push({ id: input.value, quantity: 1 });
          });
          // Replace the fetch body with JSON containing items
          this.#submitMultiple(items, formData);
          return;
        }
      }
    } catch (e) {
      console.warn('Upsell collection failed', e);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

  const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        // Expect JSON; safer than relying on text/html then parsing as JSON
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
        // Reset in-flight & restore button state
        this.#inFlight = false;
        if (!this.#buttonWasDisabled) {
          // Re-enable only if it wasn't disabled before submission
            this.refs.addToCartButtonContainer?.enable();
        }
      });
  }

  /**
   * Submit multiple items (main + upsells) in one request.
   * @param {{id: string|number, quantity: number}[]} items
   * @param {FormData} originalFormData
   */
  #submitMultiple(items, originalFormData) {
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const debug = Boolean(window.__upsellPickerDebug);

    const submitSingle = async (item, { includeSections, carryOverFormData }) => {
      /** @type {FormData} */
      const fd = new FormData();

      if (carryOverFormData) {
        // Copy all fields except id/quantity/sections so line-item properties & selling plans remain.
        originalFormData.forEach((value, key) => {
          if (key === 'id' || key === 'quantity' || key === 'sections') return;
          fd.append(key, value);
        });
      }

      fd.set('id', String(item.id));
      fd.set('quantity', String(item.quantity || 1));

      if (includeSections && cartItemComponentsSectionIds.length) {
        fd.set('sections', cartItemComponentsSectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: fd });
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      return res.json();
    };

    (async () => {
      try {
        if (!Array.isArray(items) || items.length === 0) return;

        // Base (or alternative) is always the first item; preserve form properties.
        // Upsells are added as plain variant ids to avoid leaking base properties/selling_plan.
        /** @type {any} */
        let lastResponse = null;

        for (let i = 0; i < items.length; i++) {
          const isLast = i === items.length - 1;
          const isFirst = i === 0;
          const response = await submitSingle(items[i], {
            includeSections: isLast,
            carryOverFormData: isFirst,
          });

          if (debug) {
            window.__lastUpsellPickerSubmitMultiple = {
              step: i + 1,
              total: items.length,
              item: items[i],
              isLast,
              response,
            };
            console.warn('[UpsellPicker] submitMultiple step', window.__lastUpsellPickerSubmitMultiple);
          }

          lastResponse = response;

          if (response && response.status) {
            this.dispatchEvent(
              new CartErrorEvent(
                this.querySelector('form')?.getAttribute('id') || '',
                response.message,
                response.description,
                response.errors
              )
            );

            if (addToCartTextError) {
              addToCartTextError.classList.remove('hidden');
              const textNode = addToCartTextError.childNodes[2];
              if (textNode) {
                textNode.textContent = response.message;
              } else {
                const newTextNode = document.createTextNode(response.message);
                addToCartTextError.appendChild(newTextNode);
              }
            }

            this.#setLiveRegionText(response.message);
            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, 10000);

            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: items.reduce((sum, it) => sum + Number(it.quantity || 1), 0),
                productId: this.dataset.productId,
              })
            );

            return;
          }
        }

        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), 5000);
        }

        const firstId = items[0] && items[0].id != null ? String(items[0].id) : '';
        this.dispatchEvent(
          new CartAddEvent({}, firstId, {
            source: 'product-form-component',
            itemCount: items.reduce((sum, it) => sum + Number(it.quantity || 1), 0),
            productId: this.dataset.productId,
            sections: lastResponse?.sections,
          })
        );
      } catch (error) {
        console.error(error);
      } finally {
        cartPerformance.measureFromEvent('add:user-action', new Event('add'));
        // Reset in-flight & restore button state (multi-item path)
        this.#inFlight = false;
        if (!this.#buttonWasDisabled) {
          this.refs.addToCartButtonContainer?.enable();
        }
      }
    })();
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/**
 * Global fallback: some templates/apps submit add-to-cart forms without using <product-form-component>'s
 * handleSubmit hook. When an upsell picker selection needs multi-item add or alternative swap,
 * intercept the submit at capture phase and perform the correct /cart/add.js request.
 */
(() => {
  if (window.__upsellPickerGlobalSubmitInstalled) return;
  window.__upsellPickerGlobalSubmitInstalled = true;

  /** @type {Map<string, string>} */
  const variantIdCache = window.__upsellVariantIdCache || new Map();
  window.__upsellVariantIdCache = variantIdCache;

  /**
   * Resolve a product handle to a variant id by calling Shopify's product JSON endpoint.
   * This avoids Liquid's all_products limits on pages with many referenced products.
   * @param {string} handle
   * @returns {Promise<string | null>}
   */
  const resolveVariantIdFromHandle = async (handle) => {
    const h = (handle || '').trim();
    if (!h) return null;
    if (variantIdCache.has(h)) return variantIdCache.get(h) || null;

    try {
      const res = await fetch(`/products/${encodeURIComponent(h)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const json = await res.json();
      const variants = Array.isArray(json?.variants) ? json.variants : [];
      const variant = variants.find((v) => v && v.available) || variants[0];
      const id = variant?.id != null ? String(variant.id) : null;
      if (id) variantIdCache.set(h, id);
      return id;
    } catch {
      return null;
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLElement | null}
   */
  const findRelevantUpsellPicker = (form) => {
    const productFormComponent = form.closest('product-form-component');
    const productDetailsRoot = productFormComponent?.closest('.product-details') || form.closest('.product-details');
    const gridRoot =
      productFormComponent?.closest('[data-product-grid-content]') ||
      form.closest('[data-product-grid-content]') ||
      productFormComponent?.closest('product-information') ||
      form.closest('product-information');

    /** @type {HTMLElement[]} */
    let pickers = [];
    if (productDetailsRoot) {
      pickers = Array.from(productDetailsRoot.querySelectorAll('variant-picker-product-upsell')).filter(
        (n) => n instanceof HTMLElement
      );
    }
    if (!pickers.length && gridRoot) {
      pickers = Array.from(gridRoot.querySelectorAll('variant-picker-product-upsell')).filter((n) => n instanceof HTMLElement);
    }
    if (!pickers.length) {
      pickers = Array.from(document.querySelectorAll('variant-picker-product-upsell')).filter((n) => n instanceof HTMLElement);
    }

    // Exclude quick-add contexts; this handler is intended for page-level forms.
    pickers = pickers.filter((el) => !el.closest('quick-add-dialog') && !el.closest('quick-add-component'));
    if (!pickers.length) return null;

    // Prefer one with a non-base selection.
    const bySelection = pickers.find((p) => {
      const checked = /** @type {HTMLInputElement | null} */ (p.querySelector('input[type="radio"]:checked'));
      const optionType = checked?.dataset?.optionType || null;
      return optionType && optionType !== 'base';
    });
    if (bySelection) return bySelection;

    // Prefer matching product id if available.
    const productId = productFormComponent?.dataset?.productId;
    if (productId) {
      const byProduct = pickers.find((p) => p.getAttribute('data-product-id') === String(productId));
      if (byProduct) return byProduct;
    }

    return pickers[0];
  };

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement} upsellPicker
   * @returns {{ items: {id: string|number, quantity: number}[], optionType: string, productId?: string } | null}
   */
  const buildItemsFromPicker = async (form, upsellPicker) => {
    const checked = /** @type {HTMLInputElement | null} */ (upsellPicker.querySelector('input[type="radio"]:checked'));
    const optionType = checked?.dataset?.optionType || null;
    if (!optionType || optionType === 'base') return null;

    const formData = new FormData(form);
    const mainIdEntry = formData.get('id');
    const mainId = typeof mainIdEntry === 'string' ? mainIdEntry : mainIdEntry ? String(mainIdEntry) : '';
    const quantity = Number(formData.get('quantity')) || 1;

    const ds = /** @type {any} */ (upsellPicker).dataset || {};
    let upsell1Id = ds.upsell1Id;
    let upsell2Id = ds.upsell2Id;
    let upsell3aId = ds.upsell3aId;
    let upsell3bId = ds.upsell3bId;
    let alternativeId = ds.alternativeId;

    const upsell1Handle = ds.upsell1Handle;
    const upsell2Handle = ds.upsell2Handle;
    const upsell3aHandle = ds.upsell3aHandle;
    const upsell3bHandle = ds.upsell3bHandle;
    const alternativeHandle = ds.alternativeHandle;

    // Resolve ids via /products/{handle}.js when Liquid did not provide them.
    if (optionType === 'option2' || optionType === 'option3') {
      if (!upsell1Id) upsell1Id = await resolveVariantIdFromHandle(String(upsell1Handle || ''));
    }
    if (optionType === 'option3') {
      if (!upsell2Id) upsell2Id = await resolveVariantIdFromHandle(String(upsell2Handle || ''));
    }
    if (optionType === 'option4') {
      if (!upsell3aId) upsell3aId = await resolveVariantIdFromHandle(String(upsell3aHandle || ''));
      if (!upsell3bId) upsell3bId = await resolveVariantIdFromHandle(String(upsell3bHandle || ''));
    }
    if (optionType === 'alternative') {
      if (!alternativeId) alternativeId = await resolveVariantIdFromHandle(String(alternativeHandle || ''));
    }

    const debug = Boolean(window.__upsellPickerDebug);
    if ((optionType === 'option2' || optionType === 'option3') && !upsell1Id && debug) {
      console.warn('[UpsellPicker] Could not resolve upsell1 variant id', { upsell1Handle });
    }
    if (optionType === 'option3' && !upsell2Id && debug) {
      console.warn('[UpsellPicker] Could not resolve upsell2 variant id', { upsell2Handle });
    }
    if (optionType === 'option4' && (!upsell3aId || !upsell3bId) && debug) {
      console.warn('[UpsellPicker] Could not resolve upsell3 variant id(s)', { upsell3aHandle, upsell3bHandle });
    }

    /** @type {{id: string|number, quantity: number}[]} */
    const items = [];

    if (optionType === 'alternative') {
      if (!alternativeId) return null;
      items.push({ id: String(alternativeId), quantity });
    } else {
      if (!mainId) return null;
      items.push({ id: String(mainId), quantity });
    }

    // If an upsell option is selected but we couldn't resolve required ids, don't take over.
    // This avoids silently submitting base-only through the takeover path.
    if (optionType === 'option2' && !upsell1Id) return null;
    if (optionType === 'option3' && (!upsell1Id || !upsell2Id)) return null;
    if (optionType === 'option4' && (!upsell3aId || !upsell3bId)) return null;

    if (optionType === 'option2' && upsell1Id) items.push({ id: String(upsell1Id), quantity: 1 });
    if (optionType === 'option3') {
      if (upsell1Id) items.push({ id: String(upsell1Id), quantity: 1 });
      if (upsell2Id) items.push({ id: String(upsell2Id), quantity: 1 });
    }
    if (optionType === 'option4') {
      if (upsell3aId) items.push({ id: String(upsell3aId), quantity: 1 });
      if (upsell3bId) items.push({ id: String(upsell3bId), quantity: 1 });
    }

    const productId = upsellPicker.getAttribute('data-product-id') || form.closest('product-form-component')?.dataset?.productId;
    return { items, optionType, productId: productId || undefined };
  };

  /**
   * @param {{id: string|number, quantity: number}[]} items
   * @returns {string}
   */
  const getSectionsParam = (items) => {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    return sectionIds.join(',');
  };

  /**
   * @param {HTMLFormElement} form
   * @param {{id: string|number, quantity: number}[]} items
   * @param {{ optionType: string, productId?: string }} meta
   */
  const submitItems = async (form, items, meta) => {
    const debug = Boolean(window.__upsellPickerDebug);
    if (debug) {
      const payload = { items, meta, formId: form.getAttribute('id') || null };
      window.__lastUpsellPickerGlobalSubmit = payload;
      console.warn('[UpsellPicker] global submit takeover', payload);
    }

    const submitBtn = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"], input[type="submit"]'));
    const wasDisabled = Boolean(submitBtn?.disabled);
    if (submitBtn) submitBtn.disabled = true;

    const sections = getSectionsParam(items);
    const firstId = items[0] && items[0].id != null ? String(items[0].id) : '';
    const itemCount = items.reduce((sum, it) => sum + Number(it.quantity || 1), 0);

    const isUpsellMulti = meta.optionType === 'option2' || meta.optionType === 'option3' || meta.optionType === 'option4';

    // For upsell bundles, add sequentially (base -> upsells). This is more compatible than sending items[].
    if (isUpsellMulti && items.length > 1) {
      /**
       * @param {{id: string|number, quantity: number}} item
       * @param {boolean} includeSections
       */
      const addOne = async (item, includeSections) => {
        const fd = new FormData();
        fd.append('id', String(item.id));
        fd.append('quantity', String(item.quantity || 1));
        if (includeSections && sections) fd.append('sections', sections);

        const fetchCfg = fetchConfig('javascript', { body: fd });
        const res = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'application/json',
          },
        });
        return res.json();
      };

      let lastResponse = null;
      for (let i = 0; i < items.length; i++) {
        const includeSections = i === items.length - 1;
        // eslint-disable-next-line no-await-in-loop
        const resp = await addOne(items[i], includeSections);
        lastResponse = resp;
        if (resp?.status) {
          form.dispatchEvent(
            new CartAddEvent({}, String(items[i].id), {
              didError: true,
              source: 'upsell-picker-global-submit',
              itemCount,
              productId: meta.productId,
            })
          );
          if (submitBtn && !wasDisabled) submitBtn.disabled = false;
          return;
        }
      }

      form.dispatchEvent(
        new CartAddEvent({}, firstId, {
          source: 'upsell-picker-global-submit',
          itemCount,
          productId: meta.productId,
          sections: lastResponse?.sections,
        })
      );
    } else {
      // Alternative swap (single item) or any other case: keep one-request JSON path.
      const body = {
        items,
        sections,
      };

      const fetchCfg = fetchConfig('javascript', { body: JSON.stringify(body) });
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        method: 'POST',
        headers: {
          ...fetchCfg.headers,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      const json = await res.json();

      if (json?.status) {
        form.dispatchEvent(
          new CartAddEvent({}, firstId, {
            didError: true,
            source: 'upsell-picker-global-submit',
            itemCount,
            productId: meta.productId,
          })
        );
      } else {
        form.dispatchEvent(
          new CartAddEvent({}, firstId, {
            source: 'upsell-picker-global-submit',
            itemCount,
            productId: meta.productId,
            sections: json.sections,
          })
        );
      }
    }

    if (submitBtn && !wasDisabled) submitBtn.disabled = false;
  };

  /**
   * Fallback single-item AJAX submit to preserve cart drawer behavior when we took over the submit
   * but couldn't build a multi-item payload.
   * @param {HTMLFormElement} form
   */
  const submitSingleAjax = async (form) => {
    const debug = Boolean(window.__upsellPickerDebug);

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const submitBtn = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"], input[type="submit"]'));
    const wasDisabled = Boolean(submitBtn?.disabled);
    if (submitBtn) submitBtn.disabled = true;

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const res = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const json = await res.json();

    const idEntry = formData.get('id');
    const variantId = typeof idEntry === 'string' ? idEntry : idEntry ? String(idEntry) : '';

    if (debug) {
      console.warn('[UpsellPicker] fallback single-item AJAX submit', { formId: form.getAttribute('id') || null, variantId });
    }

    if (json?.status) {
      form.dispatchEvent(
        new CartAddEvent({}, variantId, {
          didError: true,
          source: 'upsell-picker-global-submit',
          itemCount: 1,
          variantId,
        })
      );
    } else {
      form.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'upsell-picker-global-submit',
          itemCount: 1,
          variantId,
          sections: json.sections,
        })
      );
    }

    if (submitBtn && !wasDisabled) submitBtn.disabled = false;
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      // Only consider add-to-cart forms.
      const isAddForm = form.getAttribute('data-type') === 'add-to-cart-form' || (form.getAttribute('action') || '').includes('/cart/add');
      if (!isAddForm) return;

      // Don't interfere with subscription flows.
      if (form.querySelector('[name="selling_plan"]')) return;

      const upsellPicker = findRelevantUpsellPicker(form);
      if (!upsellPicker) return;

      // Decide synchronously whether we should take over.
      const checked = /** @type {HTMLInputElement | null} */ (upsellPicker.querySelector('input[type="radio"]:checked'));
      const optionType = checked?.dataset?.optionType || null;
      const needsMulti = optionType === 'option2' || optionType === 'option3' || optionType === 'option4';
      const needsSwap = optionType === 'alternative';
      if (!needsMulti && !needsSwap) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      (async () => {
        const built = await buildItemsFromPicker(form, upsellPicker);
        if (!built) {
          // Could not build upsell items (e.g. ids couldn't be resolved).
          // Don't navigate to cart; do a single-item AJAX add to preserve cart drawer.
          await submitSingleAjax(form);
          return;
        }

        await submitItems(form, built.items, { optionType: built.optionType, productId: built.productId });
      })().catch((e) => {
        console.error('Upsell global submit failed', e);
        try {
          submitSingleAjax(form);
        } catch {
          // ignore
        }
      });
    },
    true
  );
})();

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
